import { useEffect, useState } from "react";
import { View, StyleSheet, Pressable, Image, ScrollView, useWindowDimensions } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";

const SCALE_STEPS = [0.5, 0.75, 1, 1.5, 2, 3];

type FitMode = "width" | "height" | "screen" | "manual";

interface ImagePreviewModalProps {
  url: string;
  name: string;
  onClose: () => void;
  onOpenInNewTab: () => void;
}

export function ImagePreviewModal({ url, name, onClose, onOpenInNewTab }: ImagePreviewModalProps) {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>("screen");
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  const TOOLBAR_HEIGHT = 52;
  const containerWidth = windowWidth;
  const containerHeight = windowHeight - TOOLBAR_HEIGHT;

  useEffect(() => {
    Image.getSize(
      url,
      (w, h) => setNaturalSize({ w, h }),
      () => setNaturalSize({ w: 600, h: 600 }),
    );
  }, [url]);

  const getImageDimensions = () => {
    if (!naturalSize) {
      return { width: 300, height: 300 };
    }
    const { w: natW, h: natH } = naturalSize;
    const aspect = natW / natH;

    switch (fitMode) {
      case "width": {
        const w = containerWidth * scale;
        return { width: w, height: w / aspect };
      }
      case "height": {
        const h = containerHeight * scale;
        return { width: h * aspect, height: h };
      }
      case "screen": {
        const fitScale = Math.min(containerWidth / natW, containerHeight / natH);
        const w = natW * fitScale * scale;
        const h = natH * fitScale * scale;
        return { width: w, height: h };
      }
      case "manual": {
        return { width: natW * scale, height: natH * scale };
      }
    }
  };

  const zoomIn = () => {
    const nextIdx = SCALE_STEPS.findIndex((s) => s > scale);
    if (nextIdx !== -1) {
      setScale(SCALE_STEPS[nextIdx]);
      if (fitMode === "screen") setFitMode("manual");
    }
  };

  const zoomOut = () => {
    const prevIdx = [...SCALE_STEPS].reverse().findIndex((s) => s < scale);
    if (prevIdx !== -1) {
      setScale([...SCALE_STEPS].reverse()[prevIdx]);
      if (fitMode === "screen") setFitMode("manual");
    }
  };

  const fitToScreen = () => {
    setScale(1);
    setFitMode("screen");
  };

  const fitToWidth = () => {
    setScale(1);
    setFitMode("width");
  };

  const fitToHeight = () => {
    setScale(1);
    setFitMode("height");
  };

  const dims = getImageDimensions();
  const scaleLabel = `${Math.round(scale * 100)}%`;

  return (
    <View style={[styles.container, { width: windowWidth, height: windowHeight }]}>
      {/* Toolbar */}
      <View style={styles.toolbar}>
        <ThemedText style={styles.toolbarFileName} numberOfLines={1}>
          {name}
        </ThemedText>
        <View style={styles.toolbarControls}>
          <Pressable onPress={zoomOut} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="magnify-minus-outline" size={22} color="#fff" />
          </Pressable>
          <ThemedText style={styles.scaleLabel}>{scaleLabel}</ThemedText>
          <Pressable onPress={zoomIn} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="magnify-plus-outline" size={22} color="#fff" />
          </Pressable>
          <View style={styles.separator} />
          <Pressable
            onPress={fitToWidth}
            style={[styles.toolbarButton, fitMode === "width" && styles.toolbarButtonActive]}
          >
            <MaterialCommunityIcons name="arrow-expand-horizontal" size={22} color="#fff" />
          </Pressable>
          <Pressable
            onPress={fitToHeight}
            style={[styles.toolbarButton, fitMode === "height" && styles.toolbarButtonActive]}
          >
            <MaterialCommunityIcons name="arrow-expand-vertical" size={22} color="#fff" />
          </Pressable>
          <Pressable
            onPress={fitToScreen}
            style={[styles.toolbarButton, fitMode === "screen" && styles.toolbarButtonActive]}
          >
            <MaterialCommunityIcons name="fit-to-screen-outline" size={22} color="#fff" />
          </Pressable>
          <View style={styles.separator} />
          <Pressable onPress={onOpenInNewTab} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="open-in-new" size={22} color="#fff" />
          </Pressable>
          <Pressable onPress={onClose} style={styles.toolbarButton}>
            <MaterialCommunityIcons name="close" size={22} color="#fff" />
          </Pressable>
        </View>
      </View>

      {/* Image */}
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <Image source={{ uri: url }} style={{ width: dims.width, height: dims.height }} resizeMode="contain" />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "rgba(0,0,0,0.9)",
  },
  toolbar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.6)",
    padding: 12,
    gap: 12,
  },
  toolbarFileName: {
    flex: 1,
    color: "#fff",
    fontSize: 14,
    fontWeight: "500",
  },
  toolbarControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  toolbarButton: {
    padding: 6,
    borderRadius: 4,
  },
  toolbarButtonActive: {
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  separator: {
    width: 1,
    height: 20,
    backgroundColor: "rgba(255,255,255,0.3)",
    marginHorizontal: 4,
  },
  scaleLabel: {
    color: "#fff",
    fontSize: 13,
    minWidth: 40,
    textAlign: "center",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
  },
});
